import React,{useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {motion,AnimatePresence} from 'framer-motion'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'
import {useAuth} from '../contexts/AuthContext'

const {FiX,FiMail,FiLock,FiUser,FiAlertCircle}=FiIcons

function AuthModal({isOpen,onClose,defaultMode='signin'}) {
  const navigate=useNavigate()
  const [mode,setMode]=useState(defaultMode)
  const [email,setEmail]=useState('')
  const [password,setPassword]=useState('')
  const [firstName,setFirstName]=useState('')
  const [lastName,setLastName]=useState('')
  const [error,setError]=useState('')
  const [loading,setLoading]=useState(false)
  const [success,setSuccess]=useState(false)
  const {signIn,signUp}=useAuth()

  const handleSubmit=async (e)=> {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      if (mode==='signin') {
        const {error}=await signIn(email,password)
        if (error) throw error
        
        // Close modal and redirect to dashboard
        onClose()
        navigate('/dashboard')
      } else {
        if (!firstName || !lastName) {
          throw new Error('Please enter your first and last name')
        }

        const {error}=await signUp(email,password,{
          first_name: firstName,
          last_name: lastName
        })
        if (error) throw error
        
        setSuccess(true)
        setTimeout(()=> {
          onClose()
          navigate('/dashboard')
        },2000)
      }
    } catch (err) {
      setError(err.message || 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  const resetForm=()=> {
    setEmail('')
    setPassword('')
    setFirstName('')
    setLastName('')
    setError('')
    setSuccess(false)
  }

  const switchMode=(newMode)=> {
    setMode(newMode)
    resetForm()
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{opacity: 0}}
        animate={{opacity: 1}}
        exit={{opacity: 0}}
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{opacity: 0,scale: 0.95}}
          animate={{opacity: 1,scale: 1}}
          exit={{opacity: 0,scale: 0.95}}
          className="bg-white rounded-xl shadow-xl max-w-md w-full"
          onClick={(e)=> e.stopPropagation()}
        >
          <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-space-grotesk font-bold text-secondary">
                {mode==='signin' ? 'Welcome Back' : 'Create Account'}
              </h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>

            {success ? (
              <motion.div
                initial={{opacity: 0,y: 20}}
                animate={{opacity: 1,y: 0}}
                className="text-center py-8"
              >
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiUser} className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  Account Created!
                </h3>
                <p className="text-gray-600">
                  Check your email to verify your account and get started.
                </p>
              </motion.div>
            ) : (
              <>
                {/* Error Message */}
                {error && (
                  <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2">
                    <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  {mode==='signup' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          First Name
                        </label>
                        <div className="relative">
                          <SafeIcon icon={FiUser} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                          <input
                            type="text"
                            value={firstName}
                            onChange={(e)=> setFirstName(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            placeholder="Alex"
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Last Name
                        </label>
                        <div className="relative">
                          <SafeIcon icon={FiUser} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                          <input
                            type="text"
                            value={lastName}
                            onChange={(e)=> setLastName(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            placeholder="Rodriguez"
                            required
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <div className="relative">
                      <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e)=> setEmail(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="you@example.com"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="password"
                        value={password}
                        onChange={(e)=> setPassword(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="••••••••"
                        required
                        minLength={6}
                      />
                    </div>
                    {mode==='signup' && (
                      <p className="text-xs text-gray-500 mt-1">
                        Must be at least 6 characters
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-primary text-white py-3 rounded-lg hover:bg-blue-700 transition-colors btn-primary font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Please wait...' : mode==='signin' ? 'Sign In' : 'Create Account'}
                  </button>
                </form>

                {/* Switch Mode */}
                <div className="mt-6 text-center">
                  <p className="text-gray-600">
                    {mode==='signin' ? "Don't have an account?" : 'Already have an account?'}
                    {' '}
                    <button
                      onClick={()=> switchMode(mode==='signin' ? 'signup' : 'signin')}
                      className="text-primary font-semibold hover:underline"
                    >
                      {mode==='signin' ? 'Sign Up' : 'Sign In'}
                    </button>
                  </p>
                </div>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default AuthModal